/**
 * Expanded Job View Component
 * Shows detailed artifact information when a job is expanded
 * Now with tabs for Artifacts, Entities, and Chunks
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '../UI/Badge';
import { Button } from '../UI/Button';
import { FileText, CheckCircle, Clock, ChevronDown, ChevronRight, Database, X, Table, FileCode, AlertCircle } from 'lucide-react';
import { queryEntities } from '../../api/entities';
import { getArtifactChunks } from '../../api/artifacts';
import { getJobChunks } from '../../api/jobs';
import { useWorkspace } from '../../hooks/useWorkspace';

interface ArtifactItemProps {
  artifact: any;
  jobStatus: string;
  jobCompleted: boolean;
}

function ArtifactItem({ artifact, jobStatus, jobCompleted }: ArtifactItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const sizeMB = ((artifact.file_size_bytes || 0) / 1024 / 1024).toFixed(2);
  
  const isProcessed = jobCompleted && jobStatus === 'completed';
  const isProcessing = ['pending', 'queued', 'running'].includes(jobStatus);
  const isFailed = jobStatus === 'failed';

  return (
    <div className="bg-white border border-gray-200 rounded-lg hover:border-blue-300 transition-colors w-full">
      <div className="p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
              <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span className="font-medium text-gray-900 dark:text-gray-100 truncate">{artifact.filename}</span>
              <Badge>{artifact.artifact_type}</Badge>
              <span className="text-xs text-gray-500">{sizeMB} MB</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-gray-500">Processing Status</div>
                <div className="font-semibold">
                  {isProcessed ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Processed
                    </span>
                  ) : isProcessing ? (
                    <span className="text-blue-600 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Processing
                    </span>
                  ) : isFailed ? (
                    <span className="text-red-600 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Failed
                    </span>
                  ) : (
                    <span className="text-gray-400 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Pending
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Job Status</div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 capitalize">
                  {jobStatus}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Source</div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {artifact.source_type || 'upload'}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Created</div>
                <div className="font-semibold text-gray-900 dark:text-gray-100">
                  {new Date(artifact.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <div className="border-t bg-gray-50 p-3 space-y-2">
          <div className="text-xs text-gray-600">
            <div><strong>Artifact ID:</strong> {artifact.id}</div>
            <div><strong>File Size:</strong> {sizeMB} MB</div>
            <div><strong>Storage Path:</strong> {artifact.storage_path || 'N/A'}</div>
          </div>
        </div>
      )}
    </div>
  );
}

interface EntityTableProps {
  entities: any[];
  entityType: string;
  isLoading: boolean;
}

function EntityTable({ entities, entityType, isLoading }: EntityTableProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8 text-gray-500">
        Loading {entityType} entities...
      </div>
    );
  }

  if (entities.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No {entityType} entities found
      </div>
    );
  }

  // Get all unique keys from all entities to build table columns
  const allKeys = new Set<string>();
  entities.forEach(entity => {
    if (entity.data) {
      Object.keys(entity.data).forEach(key => allKeys.add(key));
    }
  });
  const columns = Array.from(allKeys).sort();

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 bg-white">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              ID
            </th>
            {columns.map(column => (
              <th key={column} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </th>
            ))}
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {entities.map((entity) => (
            <tr key={entity.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 whitespace-nowrap text-xs font-mono text-gray-600">
                {entity.id.substring(0, 8)}...
              </td>
              {columns.map(column => (
                <td key={column} className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                  {entity.data?.[column] !== undefined && entity.data[column] !== null
                    ? typeof entity.data[column] === 'object'
                      ? JSON.stringify(entity.data[column])
                      : String(entity.data[column])
                    : '-'}
                </td>
              ))}
              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                {new Date(entity.created_at).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500 border-t">
        Showing {entities.length} {entityType} entities
      </div>
    </div>
  );
}

interface ChunkTableProps {
  chunks: any[];
  isLoading: boolean;
  filters: {
    contains_table?: boolean;
    contains_figure?: boolean;
  };
  onFilterChange: (filters: {
    contains_table?: boolean;
    contains_figure?: boolean;
  }) => void;
}

function ChunkTable({ chunks, isLoading, filters, onFilterChange }: ChunkTableProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8 text-gray-500">
        Loading chunks...
      </div>
    );
  }

  // Parse markdown table to rows and columns
  const parseMarkdownTable = (content: string): { headers: string[]; rows: string[][] } | null => {
    const lines = content.trim().split('\n').filter(line => line.trim());
    if (lines.length < 2) return null;
    
    // Check if it's a markdown table (has | separator)
    const firstLine = lines[0];
    if (!firstLine.includes('|')) return null;
    
    // Extract headers
    const headers = firstLine.split('|').map(h => h.trim()).filter(h => h);
    
    // Skip separator line (---)
    let startIdx = 1;
    if (lines[1].includes('---')) {
      startIdx = 2;
    }
    
    // Extract rows
    const rows: string[][] = [];
    for (let i = startIdx; i < lines.length; i++) {
      const row = lines[i].split('|').map(cell => cell.trim()).filter(cell => cell);
      if (row.length === headers.length) {
        rows.push(row);
      }
    }
    
    return rows.length > 0 ? { headers, rows } : null;
  };

  // Render chunk content - handle tables specially
  const renderChunkContent = (chunk: any) => {
    const hasTable = chunk.meta?.contains_table === true;
    const hasFigure = chunk.meta?.contains_figure === true;
    
    if (hasTable) {
      // Try to parse as markdown table
      const tableData = parseMarkdownTable(chunk.content);
      if (tableData) {
        return (
          <div className="max-w-full overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-300 text-xs">
              <thead className="bg-gray-100">
                <tr>
                  {tableData.headers.map((header, idx) => (
                    <th key={idx} className="px-2 py-1 text-left font-medium text-gray-700 border-r border-gray-300 last:border-r-0">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tableData.rows.slice(0, 10).map((row, rowIdx) => (
                  <tr key={rowIdx} className="hover:bg-gray-50">
                    {row.map((cell, cellIdx) => (
                      <td key={cellIdx} className="px-2 py-1 text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-700 last:border-r-0 whitespace-nowrap">
                        {cell || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {tableData.rows.length > 10 && (
              <div className="text-xs text-gray-500 mt-1 px-2">
                Showing 10 of {tableData.rows.length} rows
              </div>
            )}
          </div>
        );
      }
    }
    
    // Default: show preview
    const preview = chunk.content?.substring(0, 200) || 'No content';
    return (
      <div className="max-w-md">
        <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
          {preview}
          {chunk.content && chunk.content.length > 200 && '...'}
        </div>
        {hasTable && (
          <Badge variant="default" className="mt-1 text-xs">
            Table
          </Badge>
        )}
        {hasFigure && (
          <Badge variant="default" className="mt-1 text-xs ml-1">
            Figure
          </Badge>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="text-xs font-medium text-gray-700 mb-2">Filter Chunks:</div>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.contains_table === true}
              onChange={(e) => onFilterChange({
                ...filters,
                contains_table: e.target.checked ? true : undefined,
              })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Contains Tables</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.contains_figure === true}
              onChange={(e) => onFilterChange({
                ...filters,
                contains_figure: e.target.checked ? true : undefined,
              })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Contains Figures</span>
          </label>
          {(filters.contains_table !== undefined || filters.contains_figure !== undefined) && (
            <button
              onClick={() => onFilterChange({})}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {chunks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No chunks found
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 bg-white">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Chunk ID
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Artifact
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Content Preview
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Strategy
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Position
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {chunks.map((chunk) => (
            <tr key={chunk.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 whitespace-nowrap text-xs font-mono text-gray-600">
                {chunk.id?.substring(0, 8) || 'N/A'}...
              </td>
              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                {chunk.artifact?.filename || chunk.artifact_id?.substring(0, 8) || 'N/A'}
              </td>
              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-2xl">
                {renderChunkContent(chunk)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                {chunk.strategy || 'N/A'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                {chunk.position !== undefined ? chunk.position : 'N/A'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                {chunk.created_at ? new Date(chunk.created_at).toLocaleDateString() : 'N/A'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500 border-t">
        Showing {chunks.length} chunks
      </div>
    </div>
      )}
    </div>
  );
}

interface JobExpandedViewProps {
  job: any;
  artifactIds: string[];
  artifacts: any[];
}

export function JobExpandedView({ job, artifactIds, artifacts }: JobExpandedViewProps) {
  const { projectId } = useWorkspace();
  const [activeTab, setActiveTab] = useState<'artifacts' | 'entities' | 'chunks'>('artifacts');
  const [activeEntityType, setActiveEntityType] = useState<string | null>(null);

  const artifactMap = new Map(artifacts.map(a => [a.id, a]));
  const jobArtifacts = artifactIds
    .map(id => artifactMap.get(id))
    .filter(Boolean);

  const outputData = job.output_data || {};
  const inputData = job.input_data || {};

  // Get entity types from job output
  const entityTypes = outputData.entities_extracted 
    ? Object.keys(outputData.entities_extracted)
    : [];

  // Fetch entities for all artifacts in the job
  const entityQueries = entityTypes.map(entityType => 
    useQuery({
      queryKey: ['job-entities', projectId, job.id, entityType],
      queryFn: async () => {
        // Query entities for this type from all artifacts in the job
        const allEntities: any[] = [];
        for (const artifactId of artifactIds) {
          try {
            const result = await queryEntities({
              project_id: projectId!,
              entity_type: entityType,
              filters: { source_artifact_id: artifactId },
              limit: 1000,
            });
            allEntities.push(...(result.entities || []));
          } catch (error) {
            console.error(`Error fetching ${entityType} entities for artifact ${artifactId}:`, error);
          }
        }
        return allEntities;
      },
      enabled: activeTab === 'entities' && !!projectId && artifactIds.length > 0,
    })
  );

  // Chunk filters
  const [chunkFilters, setChunkFilters] = useState<{
    contains_table?: boolean;
    contains_figure?: boolean;
  }>({});

  // Fetch chunks for the job (using the new endpoint)
  const { data: allChunks, isLoading: chunksLoading } = useQuery({
    queryKey: ['job-chunks', job.id, chunkFilters],
    queryFn: () => getJobChunks(job.id, chunkFilters),
    enabled: activeTab === 'chunks' && !!job.id,
  });

  // Map chunks with artifact info
  const chunksWithArtifacts = (allChunks || []).map(chunk => ({
    ...chunk,
    artifact: jobArtifacts.find(a => a.id === chunk.artifact_id),
  }));

  // Set default entity type when switching to entities tab
  React.useEffect(() => {
    if (activeTab === 'entities' && entityTypes.length > 0 && !activeEntityType) {
      setActiveEntityType(entityTypes[0]);
    }
  }, [activeTab, entityTypes, activeEntityType]);

  const entityDataMap = new Map(
    entityQueries.map((q, idx) => [entityTypes[idx], q])
  );

  const activeEntityData = activeEntityType 
    ? entityDataMap.get(activeEntityType)?.data || []
    : [];
  const activeEntityLoading = activeEntityType
    ? entityDataMap.get(activeEntityType)?.isLoading || false
    : false;

  return (
    <div className="mt-4 space-y-4 border-t pt-4 w-full">
      {/* Warnings */}
      {outputData.warnings && outputData.warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium text-yellow-900 mb-2">Processing Warnings</div>
              <ul className="list-disc list-inside text-sm text-yellow-800 space-y-1">
                {outputData.warnings.map((warning: string, idx: number) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Job Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {outputData.num_artifacts_processed !== undefined && (
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-xs text-blue-700 mb-1">Artifacts Processed</div>
            <div className="text-lg font-semibold text-blue-900">
              {outputData.num_artifacts_processed}
            </div>
          </div>
        )}
        {outputData.num_chunks_created !== undefined && (
          <div className={`rounded-lg p-3 ${outputData.num_chunks_created === 0 && outputData.num_artifacts_processed > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50'}`}>
            <div className={`text-xs mb-1 ${outputData.num_chunks_created === 0 && outputData.num_artifacts_processed > 0 ? 'text-red-700' : 'text-gray-600'}`}>
              Chunks Created
            </div>
            <div className={`text-lg font-semibold ${outputData.num_chunks_created === 0 && outputData.num_artifacts_processed > 0 ? 'text-red-900 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
              {outputData.num_chunks_created.toLocaleString()}
            </div>
            {outputData.num_chunks_created === 0 && outputData.num_artifacts_processed > 0 && (
              <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                No chunks created
              </div>
            )}
          </div>
        )}
        {outputData.num_evidence_entries !== undefined && (
          <div className="bg-purple-50 rounded-lg p-3">
            <div className="text-xs text-gray-600 mb-1">Evidence Entries</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {outputData.num_evidence_entries.toLocaleString()}
            </div>
          </div>
        )}
        {outputData.total_entities_created !== undefined && (
          <div className={`rounded-lg p-3 ${outputData.total_entities_created === 0 && outputData.num_artifacts_processed > 0 ? 'bg-red-50 border border-red-200' : 'bg-orange-50'}`}>
            <div className={`text-xs mb-1 ${outputData.total_entities_created === 0 && outputData.num_artifacts_processed > 0 ? 'text-red-700' : 'text-gray-600'}`}>
              Entities Created
            </div>
            <div className={`text-lg font-semibold ${outputData.total_entities_created === 0 && outputData.num_artifacts_processed > 0 ? 'text-red-900 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
              {outputData.total_entities_created.toLocaleString()}
            </div>
            {outputData.total_entities_created === 0 && outputData.num_artifacts_processed > 0 && (
              <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                No entities created
              </div>
            )}
          </div>
        )}
      </div>

      {/* Entity Type Breakdown */}
      {outputData.entities_extracted && Object.keys(outputData.entities_extracted).length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Entities by Type</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(outputData.entities_extracted).map(([type, count]: [string, any]) => (
              <Badge key={type} variant="default">
                {type}: {count}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('artifacts')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'artifacts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Artifacts ({jobArtifacts.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('entities')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'entities'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
            disabled={entityTypes.length === 0}
          >
            <div className="flex items-center gap-2">
              <Table className="h-4 w-4" />
              Entities ({outputData.total_entities_created || 0})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('chunks')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'chunks'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <div className="flex items-center gap-2">
              <FileCode className="h-4 w-4" />
              Chunks ({outputData.num_chunks_created || 0})
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="w-full">
        {activeTab === 'artifacts' && (
          <div className="space-y-2 max-h-96 overflow-y-auto w-full">
            {jobArtifacts.length > 0 ? (
              jobArtifacts.map((artifact) => (
                <ArtifactItem 
                  key={artifact.id} 
                  artifact={artifact}
                  jobStatus={job.status}
                  jobCompleted={job.status === 'completed'}
                />
              ))
            ) : (
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                Loading artifact information...
              </div>
            )}
          </div>
        )}

        {activeTab === 'entities' && (
          <div className="w-full">
            {entityTypes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No entities were extracted in this job
              </div>
            ) : (
              <>
                {/* Entity Type Sub-tabs */}
                <div className="border-b border-gray-200 mb-4">
                  <nav className="flex space-x-4" aria-label="Entity Type Tabs">
                    {entityTypes.map((type) => {
                      const count = outputData.entities_extracted?.[type] || 0;
                      return (
                        <button
                          key={type}
                          onClick={() => setActiveEntityType(type)}
                          className={`
                            py-2 px-3 border-b-2 font-medium text-xs
                            ${activeEntityType === type
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }
                          `}
                        >
                          {type.replace(/_/g, ' ')} ({count})
                        </button>
                      );
                    })}
                  </nav>
                </div>

                {/* Entity Table */}
                {activeEntityType && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <EntityTable
                      entities={activeEntityData}
                      entityType={activeEntityType}
                      isLoading={activeEntityLoading}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'chunks' && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <ChunkTable 
              chunks={chunksWithArtifacts} 
              isLoading={chunksLoading}
              filters={chunkFilters}
              onFilterChange={setChunkFilters}
            />
          </div>
        )}
      </div>

      {/* Job Configuration */}
      {inputData.config && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Job Configuration</h4>
          <div className="text-xs text-gray-600 space-y-1">
            {inputData.config.chunk_strategy && (
              <div>Chunk Strategy: <span className="font-semibold">{inputData.config.chunk_strategy}</span></div>
            )}
            {inputData.config.index_types && (
              <div>Index Types: <span className="font-semibold">{inputData.config.index_types.join(', ')}</span></div>
            )}
            {inputData.config.parse_tables !== undefined && (
              <div>Parse Tables: <span className="font-semibold">{inputData.config.parse_tables ? 'Yes' : 'No'}</span></div>
            )}
            {inputData.config.parse_figures !== undefined && (
              <div>Parse Figures: <span className="font-semibold">{inputData.config.parse_figures ? 'Yes' : 'No'}</span></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
